import { getSessionToken, removeSessionToken } from './auth';
import { refreshSessionToken } from './auth-refresh';

export interface ApiClientOptions {
  baseURL?: string;
  onUnauthorized?: () => void;
  enableAutoRefresh?: boolean; // Enable automatic token refresh on 401
  debug?: boolean; // Enable debug logging for all API calls
}

export interface ApiRequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined | null>;
}

export interface ApiClient {
  get<T>(url: string, config?: ApiRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T>;
  put<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T>;
  delete<T>(url: string, config?: ApiRequestConfig): Promise<T>;
  patch<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T>;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const { baseURL = '/api', onUnauthorized, enableAutoRefresh = true, debug = false } = options;

  const request = async <T>(
    method: string,
    url: string,
    data?: unknown,
    config?: ApiRequestConfig
  ): Promise<T> => {
    const startTime = debug ? performance.now() : 0;

    let sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      ...(config?.headers ?? {}),
    };

    if (debug) {
      console.log(`[SDK] 🚀 ${method} ${url}`, {
        data,
        params: config?.params,
        hasToken: !!sessionToken,
      });
    }

    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    if (!isFormData && data !== undefined) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    }
    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`;
    } else {
      // Debug logging for missing token
      console.warn(`[API] No session token found for ${method} ${url}`);
    }

    const params = config?.params ?? {};
    const queryString = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');
    const requestUrl = `${baseURL}${url}${queryString ? `?${queryString}` : ''}`;

    let response = await fetch(requestUrl, {
      method,
      headers,
      credentials: 'include', // CRITICAL: Send cookies for session authentication
      body: data === undefined ? undefined : isFormData ? (data as FormData) : JSON.stringify(data),
    });

    // Auto-refresh on 401 if enabled
    if (response.status === 401 && enableAutoRefresh && sessionToken) {
      console.log('[API] 401 Unauthorized, attempting token refresh...');

      const newToken = await refreshSessionToken(baseURL);

      if (newToken) {
        console.log('[API] Token refreshed, retrying request...');
        headers.Authorization = `Bearer ${newToken}`;

        // Retry the request with new token
        response = await fetch(requestUrl, {
          method,
          headers,
          credentials: 'include',
          body: data === undefined ? undefined : isFormData ? (data as FormData) : JSON.stringify(data),
        });
      }
    }

    // If still 401 after refresh attempt, handle unauthorized
    if (response.status === 401) {
      console.warn(`[API] 401 Unauthorized for ${method} ${url} (after refresh attempt)`, {
        hadToken: Boolean(sessionToken),
        tokenLength: sessionToken?.length ?? 0,
        url,
      });
      removeSessionToken();
      if (onUnauthorized) {
        onUnauthorized();
      } else if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }

    const contentType = response.headers.get('Content-Type') || '';
    const payload = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const message =
        typeof payload === 'object' && payload !== null
          ? (payload as { error?: string; message?: string }).error ||
            (payload as { error?: string; message?: string }).message ||
            `Request failed with status ${response.status}`
          : `Request failed with status ${response.status}`;

      if (debug) {
        const duration = performance.now() - startTime;
        console.error(`[SDK] ❌ ${method} ${url} failed (${duration.toFixed(2)}ms)`, {
          status: response.status,
          message,
        });
      }

      throw new Error(message);
    }

    if (debug) {
      const duration = performance.now() - startTime;
      console.log(`[SDK] ✅ ${method} ${url} (${duration.toFixed(2)}ms)`, {
        status: response.status,
        dataSize: typeof payload === 'string' ? payload.length : JSON.stringify(payload).length,
      });
    }

    return payload as T;
  };

  return {
    async get<T>(url: string, config?: ApiRequestConfig): Promise<T> {
      return request<T>('GET', url, undefined, config);
    },
    async post<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
      return request<T>('POST', url, data, config);
    },
    async put<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
      return request<T>('PUT', url, data, config);
    },
    async delete<T>(url: string, config?: ApiRequestConfig): Promise<T> {
      return request<T>('DELETE', url, undefined, config);
    },
    async patch<T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
      return request<T>('PATCH', url, data, config);
    },
  };
}
